import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';

import { withFlags } from '../../../flags/Flags';

import {
  Button,
  Combobox,
  Checkbox,
  Dropdown,
  Icon,
  Tooltip
} from '@salesforce/design-system-react';

import './AttributeMappingRow.scss';

class AttributeMappingRow extends Component {
  comboboxOptions(type) {
    const { [`${type}Key`]: typeKey, [`${type}Keys`]: typeKeys } = this.props;
    const selectedOption = typeKey ? [{ id: typeKey, label: typeKey }] : [];
    const availableKeys = (typeKeys || []).map(key => {
      return { id: key, label: key };
    });
    return [...selectedOption, ...availableKeys];
  }

  handleSelect(type) {
    return (evt, data) => {
      const { mappingRules, assetKey } = this.props;

      if (data.selection.length > 0) {
        const rules = mappingRules || {};
        let replaceAll = false;
        let update = {};

        switch (type) {
          case 'asset':
            replaceAll = true;

            Object.keys(rules).forEach(key => {
              // keep the asset's index position
              if (key === assetKey) {
                update = {
                  ...update,
                  ...this.createMapping(data.selection[0].id)
                };
              } else {
                update = { ...update, [key]: rules[key] };
              }
            });

            if (!assetKey) {
              update = {
                ...update,
                ...this.createMapping(data.selection[0].id)
              };
              this.props.onDeleteRule();
            }
            break;

          case 'profile':
            update = {
              [assetKey]: {
                ...rules[assetKey],
                consumerKey: data.selection[0].id
              }
            };
            break;

          default:
            break;
        }

        this.props.onRuleChange(update, replaceAll);
      }
    };
  }

  handleCheckbox(field, assetKey, checked) {
    const { mappingRules } = this.props;
    let fallback = {};

    if (field === 'mustMatch' && !checked) {
      fallback = {
        applyToFallback: false
      };
    }

    const update = {
      [assetKey]: {
        ...mappingRules[assetKey],
        [field]: checked,
        ...fallback
      }
    };
    this.props.onRuleChange(update);
  }

  toggleApplyToFallback() {
    const { applyToFallback, assetKey } = this.props;
    this.handleCheckbox('applyToFallback', assetKey, !applyToFallback);
  }

  findProfileMatch(assetKey) {
    const { profileKeys } = this.props;
    const match = profileKeys.find(profile => {
      return profile.toLowerCase() === assetKey.toLowerCase();
    });

    return match;
  }

  createMapping(selection) {
    const { profileKey } = this.props;
    const profileMatch = this.findProfileMatch(selection);

    if (!profileMatch && !profileKey) {
      return {
        [selection]: {}
      };
    }

    return {
      [selection]: {
        consumerKey: profileKey || profileMatch
      }
    };
  }

  currentSelection(selectionType) {
    const value = this.props[selectionType];
    return value
      ? [
          {
            id: value,
            label: value
          }
        ]
      : [];
  }

  renderDropdown(type) {
    const { formatMessage } = this.props.intl;
    const { assetKey } = this.props;
    const { profileKey } = this.props;
    const selectionType = `${type}Key`;
    const errorText =
      (!assetKey && type === 'asset') ||
      (assetKey && !profileKey && type === 'profile')
        ? formatMessage({
            id: 'components.ecs.rules.mapping.attributeRequired'
          })
        : '';

    return (
      <Combobox
        className={`mapping-${selectionType}-combobox`}
        options={this.comboboxOptions(type)}
        selection={this.currentSelection(selectionType)}
        labels={{
          label: formatMessage({
            id: `components.ecs.rules.mapping.${selectionType}`
          }),
          placeholderReadOnly: formatMessage({
            id: 'components.ecs.rules.mapping.selectAttribute'
          })
        }}
        events={{ onSelect: this.handleSelect(type) }}
        variant="readonly"
        singleInputDisabled={!assetKey && type === 'profile'}
        errorText={errorText}
      />
    );
  }

  renderDeleteButton() {
    return (
      <Button
        className="delete-mapping-button"
        iconCategory="utility"
        iconName="delete"
        iconVariant="border-filled"
        variant="icon"
        onClick={this.props.onDeleteRule}
      />
    );
  }

  renderKebabMenu() {
    const { formatMessage } = this.props.intl;
    const { applyToFallback } = this.props;

    const label = formatMessage(
      { id: 'components.ecs.rules.mapping.applyFallback' },
      { tooltip: undefined }
    );

    return (
      <Dropdown
        value={applyToFallback ? 'applyToFallback' : null}
        checkmark={applyToFallback}
        buttonVariant="icon"
        iconVariant="bare"
        iconCategory="utility"
        iconName="threedots_vertical"
        disabled={!this.props.mustMatch}
        onSelect={() => this.toggleApplyToFallback()}
        options={[{ label, value: 'applyToFallback' }]}
      />
    );
  }

  renderFallbackCheckbox() {
    const { formatMessage } = this.props.intl;
    const {
      assetKey,
      profileKey,
      mustMatch,
      applyToFallback,
      showFallbackColumn
    } = this.props;

    if (!showFallbackColumn) {
      return null;
    }

    return (
      <div className="match-cell">
        <FormattedMessage
          id="components.ecs.rules.mapping.applyFallback"
          values={{
            tooltip: (
              <Tooltip
                key={`fallback-tooltip-${assetKey}`}
                align="top"
                content={formatMessage({
                  id: 'components.ecs.rules.mapping.applyFallbackTooltip'
                })}
                variant="learnMore"
                dialogClassName="dialog-classname"
                position="overflowBoundaryElement"
              />
            )
          }}
        />

        <Checkbox
          value={applyToFallback}
          checked={applyToFallback}
          onChange={(evt, { checked }) =>
            this.handleCheckbox('applyToFallback', assetKey, checked)
          }
          disabled={!profileKey || !mustMatch}
        />
      </div>
    );
  }

  render() {
    const { formatMessage } = this.props.intl;
    const { assetKey, profileKey, mustMatch } = this.props;

    return withFlags(flags => (
      <div className="attribute-mapping-row">
        {this.renderDropdown('asset')}

        <Icon
          containerClassName="icon-cell"
          className="row-arrow"
          category="utility"
          name="forward"
          size="small"
        />

        {this.renderDropdown('profile')}

        <div className="match-cell">
          <FormattedMessage
            id="components.ecs.rules.mapping.mustMatch"
            values={{
              tooltip: (
                <Tooltip
                  key={`match-tooltip-${assetKey}`}
                  align="top"
                  content={formatMessage({
                    id: 'components.ecs.rules.mapping.matchTooltip'
                  })}
                  variant="learnMore"
                  dialogClassName="dialog-classname"
                  position="overflowBoundaryElement"
                />
              )
            }}
          />

          <Checkbox
            value={mustMatch}
            checked={mustMatch}
            onChange={(evt, { checked }) =>
              this.handleCheckbox('mustMatch', assetKey, checked)
            }
            disabled={!profileKey}
          />
        </div>

        {flags.fallbackRules ? this.renderFallbackCheckbox() : null}

        {this.renderDeleteButton()}

        {flags.fallbackRules ? this.renderKebabMenu() : null}
      </div>
    ));
  }
}

AttributeMappingRow.propTypes = {
  actions: PropTypes.object,
  assetKey: PropTypes.string,
  assetKeys: PropTypes.array,
  profileKey: PropTypes.string,
  profileKeys: PropTypes.array,
  mustMatch: PropTypes.bool,
  applyToFallback: PropTypes.bool,
  showFallbackColumn: PropTypes.bool,
  mappingRules: PropTypes.object,
  onRuleChange: PropTypes.func.isRequired,
  onDeleteRule: PropTypes.func.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(AttributeMappingRow);
